<template>
  <v-autocomplete
    v-track-click="() => trackingEvents.openSelectInput(identifier)"
    class="bm-select"
    v-model="modelValue"
    :items="items"
    :label="label"
    :name="name"
    :autocomplete="resolvedAutocomplete"
    :clearable="clearable"
    :disabled="disabled"
    :required="required"
    :rules="rules"
    :menuProps="menuProps"
    :listProps="computedListProps"
    data-cc="single-select"
  >
    <template v-if="chips" #selection="{ item }">
      <v-chip
        :title="item.title"
        size="small"
        color="primary"
        variant="tonal"
        data-cc="single-select-chip-text"
      >
        <span :class="{ 'selected-element-rtl': rtl }" class="selected-element">
          {{ item.title }}
        </span>
        <v-icon v-if="isTableSortSelect" class="ml-2">
          {{ sortingIcon }}
        </v-icon>
      </v-chip>
    </template>
    <template v-if="isTableSortSelect" #item="{ item }">
      <slot name="item" :item="item.raw" />
    </template>
  </v-autocomplete>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import trackingEvents from '@/config/trackingEvents';
import { logAppError } from '@/config/sentryConfig';

// Vuetify 3 item shapes
interface SelectItem {
  title: string;
  value: any;
}

type SelectItemInput = SelectItem | string;

interface Props {
  items?: SelectItemInput[];
  label?: string;
  rtl?: boolean;
  clearable?: boolean;
  chips?: boolean;
  disabled?: boolean;
  isTableSortSelect?: boolean;
  isSortDesc?: boolean;
  identifier?: string;
  required?: boolean;
  rules?: any[];
  menuWidth?: string | number;
  menuMaxWidth?: string | number;
  name?: string;
  autocomplete?: string;
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  label: '',
  rtl: false,
  clearable: true,
  chips: true,
  disabled: false,
  isTableSortSelect: false,
  isSortDesc: false,
  identifier: '',
  required: false,
  rules: () => []
});

const modelValue = defineModel<any | undefined>();

// Add data attributes for testing
const customTestingAttribute = computed(() =>
  props.identifier ? `select-menu-${props.identifier}` : 'select-menu'
);

const menuProps = computed(() => {
  const propsForMenu: Record<string, string | number | boolean> = {
    location: 'bottom',
    density: 'compact',
    'data-cc': customTestingAttribute.value
  };

  if (props.menuWidth != null) {
    propsForMenu.width = props.menuWidth;
  }

  return propsForMenu;
});

const computedListProps = computed(
  () =>
    ({
      'data-cc': `list-${customTestingAttribute.value}`,
      class: 'bm-select-menu-content',
      density: 'compact'
    }) as Record<string, any>
);

const resolvedAutocomplete = computed(() => {
  const normalizedAutocomplete = props.autocomplete?.trim().toLowerCase();
  const hasName = Boolean(props.name?.trim());

  if (!hasName) return 'off';
  if (normalizedAutocomplete === 'off') return 'off';

  return 'on';
});

const sortingIcon = computed(() => {
  return props.isSortDesc ? '$arrowdown' : '$arrowup';
});

// Check for legacy label-only items
watch(
  () => props.items,
  (items) => {
    if (items) {
      items.forEach((item) => {
        if (
          typeof item === 'object' &&
          item !== null &&
          'label' in item &&
          !('title' in item)
        ) {
          logAppError({
            error: new Error(`Legacy label-only item detected in BMSelect`),
            functionName: 'BMSelect',
            section: 'select',
            level: 'warning',
            message: `Item contains 'label' but not 'title'. Please migrate to Vuetify 3 format.`,
            metadata: { item }
          });
        }
      });
    }
  },
  { deep: true, immediate: true }
);
</script>

<style scoped lang="scss">
.bm-select {
  // Mimic the single-line behavior of v-select
  :deep(.v-field__input) {
    flex-wrap: nowrap;

    .v-autocomplete__selection {
      max-width: 90%;
    }
  }
}

.select-search-input {
  padding-right: 12px;

  :deep(.v-field__clearable i) {
    font-size: 16px;
  }

  :deep(.v-field__input) {
    padding-left: 16px;
  }
}

.selected-element {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &-rtl {
    direction: rtl;
    text-align: left;
  }
}
</style>
